// pages/Construction/index.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, Button, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { ConstructionList } from '../../components/Construction/ConstructionList';
import SearchModal from '../../components/Construction/SearchModal';
import { Construction, SearchParams } from '../../types/construction';
import { Member } from '../../types/member';
import { Customer } from '../../types/customer';
import { constructionService } from '../../services/construction';
import { memberService } from '../../services/member';
import { customerService } from '../../services/customer';
import { InvoiceRequestDialog } from '../../components/InvoiceRequest/InvoiceRequestDialog';
import { ScheduleSelectionDialog } from '../../components/DailyReport/ScheduleSelectionDialog';
import { SupervisorSelectionDialog } from '../../components/DailyReport/SupervisorSelectionDialog';
import { scheduleReportService } from '../../services/scheduleReportService';
import { UnreportedScheduleItem } from '../../types/commonSchedule';

// ローカルストレージのキー
const STORAGE_KEY = 'constructionSearchParams';

const ConstructionListPage: React.FC = () => {
  const [constructions, setConstructions] = useState<Construction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchParams | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [invoiceRequestDialogOpen, setInvoiceRequestDialogOpen] = useState(false);
  const [selectedConstructionId, setSelectedConstructionId] = useState<number | null>(null);
  const [lastSavedParams, setLastSavedParams] = useState<SearchParams | null>(null);

  // 日報登録関連の状態
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [supervisorDialogOpen, setSupervisorDialogOpen] = useState(false);
  const [selectedConstructionName, setSelectedConstructionName] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState<UnreportedScheduleItem | null>(null);
  const [membersData, setMembersData] = useState<Member[]>([]);
  const [unreportedSchedules, setUnreportedSchedules] = useState<UnreportedScheduleItem[]>([]);


  const navigate = useNavigate();
  const location = useLocation();

  // location と sessionStorage が主要な判断材料
  useEffect(() => {
    // 初回のロードと他のページからの遷移を両方検知
    const init = async () => {
      try {
        setLoading(true);

        // 検索条件を最初に読み込む（状態に依存した実行を避けるため）
        const savedParams = localStorage.getItem(STORAGE_KEY);
        let searchActive = false;
        let params = null;

        // 保存された検索条件があれば、それを使う
        if (savedParams) {
          try {
            const parsedParams = JSON.parse(savedParams) as SearchParams;
            if (Object.keys(parsedParams).length > 0) {
              console.log('保存された検索条件を使用:', parsedParams);
              params = parsedParams;
              searchActive = true;
            }
          } catch (e) {
            console.error('検索条件の解析エラー:', e);
          }
        }

        // 基本データを読み込む
        await Promise.all([loadMembers(), loadCustomers()]);

        // 検索条件に基づいてデータを読み込む
        if (searchActive && params) {
          // console.log('検索条件あり、検索を実行:', params);
          setSearchParams(params);
          setIsSearchActive(true);
          await searchConstructions(params);
        } else {
          // console.log('検索条件なし、全データ読み込み');
          setSearchParams(null);
          setIsSearchActive(false);
          await loadConstructions();
        }
      } catch (error) {
        console.error('初期化エラー:', error);
        setError('データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [location.key]); // location.key は他のページに遷移して戻ってきたときに変わる

  // 検索条件をローカルストレージから復元する関数
  const restoreSearchParamsFromStorage = () => {
    try {
      const savedParams = localStorage.getItem(STORAGE_KEY);
      if (savedParams) {
        const parsedParams = JSON.parse(savedParams) as SearchParams;
        if (Object.keys(parsedParams).length > 0) {
          // console.log('ローカルストレージから検索条件を復元:', parsedParams);
          setSearchParams(parsedParams);
          setIsSearchActive(true);
          return true; // 検索条件あり
        }
      }
      setIsSearchActive(false);
      setSearchParams(null);
      return false; // 検索条件なし
    } catch (error) {
      console.error('Failed to retrieve search params from localStorage:', error);
      setIsSearchActive(false);
      setSearchParams(null);
      return false;
    }
  };

  const loadConstructions = async () => {
    try {
      setLoading(true);
      setError(null);
      const constructionsData = await constructionService.getAll();
      setConstructions(constructionsData);
      return constructionsData;
    } catch (error) {
      console.error('工事データ取得エラー:', error);
      setError('データの取得に失敗しました');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      const membersData = await memberService.getAllMembers();
      setMembers(membersData);
      setMembersData(membersData); // 日報登録用にも保存
      return membersData;
    } catch (error) {
      console.error('メンバー情報取得エラー:', error);
      return [];
    }
  };

  const loadCustomers = async () => {
    try {
      const customersData = await customerService.getAllCustomers();
      setCustomers(customersData);
      return customersData;
    } catch (error) {
      console.error('顧客情報取得エラー:', error);
      return [];
    }
  };

  const searchConstructions = async (params: SearchParams) => {
    // パラメータが空の場合は全データを読み込む
    if (!params || Object.keys(params).length === 0) {
      return loadConstructions();
    }

    try {
      // console.log('検索実行開始:', params);
      setLoading(true);
      setError(null);

      const constructionsData = await constructionService.search(params);
      // console.log('検索結果:', constructionsData.length, '件取得');

      // データを更新
      setConstructions(constructionsData);
      setPage(0); // 検索結果の1ページ目を表示

      // 検索状態を更新
      setIsSearchActive(true);

      return constructionsData;
    } catch (error) {
      console.error('検索エラー:', error);
      setError('検索に失敗しました');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (params: SearchParams) => {
    // 空の条件をフィルタリング
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== '')
    ) as SearchParams;

    // 検索条件の有無チェック
    const hasSearchParams = Object.keys(filteredParams).length > 0;

    // 検索条件をローカルストレージに保存または削除
    if (hasSearchParams) {
      const paramsJson = JSON.stringify(filteredParams);
      // console.log('検索条件をローカルストレージに保存:', filteredParams);
      localStorage.setItem(STORAGE_KEY, paramsJson);
      setLastSavedParams(filteredParams);
    } else {
      // console.log('検索条件をローカルストレージから削除');
      localStorage.removeItem(STORAGE_KEY);
      setLastSavedParams(null);
    }

    // 状態を更新
    setSearchParams(filteredParams);
    setIsSearchActive(hasSearchParams);

    // 即時検索を実行
    if (hasSearchParams) {
      // console.log('検索を実行');
      await searchConstructions(filteredParams);
    } else {
      // console.log('全データを読み込み');
      await loadConstructions();
    }
  };

  const handleResetSearch = async () => {
    // console.log('検索条件をリセット');

    // ローカルストレージから検索条件を削除
    localStorage.removeItem(STORAGE_KEY);
    setLastSavedParams(null);

    // 状態をリセット
    setIsSearchActive(false);
    setSearchParams(null);

    // データを再読込
    await loadConstructions();
  };

  const handleEdit = (id: number) => {
    navigate(`/construction/${id}/edit`);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('本当に削除しますか？')) {
      try {
        await constructionService.delete(id);
        if (isSearchActive && searchParams) {
          searchConstructions(searchParams);
        } else {
          loadConstructions();
        }
      } catch (error) {
        console.error('工事データの削除に失敗しました:', error);
        setError('削除に失敗しました');
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRowExpansion = (index: number) => {
    setExpandedRows(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // 日報登録ボタンクリック時の処理（修正）
  const handleDailyReportCreate = async (constructionId: number) => {
    // ナビゲーション前に検索条件が確実に保存されているか確認
    if (isSearchActive && searchParams) {
      // console.log('ナビゲーション前に検索条件を保存:', searchParams);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searchParams));
      setLastSavedParams(searchParams);
    }

    try {
      // 選択された工事情報を取得
      const construction = constructions.find(c => c.ID === constructionId);
      if (!construction) {
        throw new Error('工事情報が見つかりません');
      }

      setSelectedConstructionId(constructionId);
      setSelectedConstructionName(
        `${construction.customerName || ''} ${construction.siteName || ''}`
      );

      // 未報告スケジュールを確認
      const unreportedResult = await scheduleReportService.getUnreportedSchedules(constructionId);

      // 取得したスケジュールを状態に保存
      if (unreportedResult.schedules) {
        setUnreportedSchedules(unreportedResult.schedules);
      }

      if (unreportedResult.schedules && unreportedResult.schedules.length > 0) {
        // 未報告スケジュールがある場合はダイアログを表示
        setScheduleDialogOpen(true);
      } else {
        // 未報告スケジュールがない場合は直接日報作成画面へ
        navigate(`/daily-report/new?constructionId=${constructionId}`);
      }
    } catch (error) {
      console.error('未報告スケジュール取得エラー:', error);
      // エラーが発生した場合も直接日報作成画面へ
      navigate(`/daily-report/new?constructionId=${constructionId}`);
    }
  };


  // スケジュール選択後の処理（新規追加）
  const handleScheduleSelect = (schedule: UnreportedScheduleItem | null) => {
    setScheduleDialogOpen(false);
    setSelectedSchedule(schedule);

    if (schedule) {
      // スケジュールが選択された場合、職長選択ダイアログを表示
      setSupervisorDialogOpen(true);
    } else if (selectedConstructionId) {
      // スケジュールなしで作成する場合は直接日報作成画面へ
      navigate(`/daily-report/new?constructionId=${selectedConstructionId}`);
    }
  };

  // 職長選択後の処理（新規追加）
  const handleSupervisorSelect = (supervisor: string) => {
    setSupervisorDialogOpen(false);

    if (selectedSchedule && selectedConstructionId) {
      // スケジュールデータと職長情報を含めて日報作成画面へ遷移
      const params = new URLSearchParams();
      params.append('constructionId', selectedConstructionId.toString());
      params.append('scheduleId', selectedSchedule.id.toString());
      params.append('scheduleType', 'construction'); // 工事スケジュールのみ対応
      params.append('supervisor', supervisor);

      navigate(`/daily-report/new?${params.toString()}`);
    }
  };

  const handleCalculationSheet = (constructionId: number) => {
    // ナビゲーション前に検索条件が確実に保存されているか確認
    if (isSearchActive && searchParams) {
      // console.log('ナビゲーション前に検索条件を保存:', searchParams);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searchParams));
      setLastSavedParams(searchParams);
    }
    navigate(`/calculation-sheet/${constructionId}`);
  };

  // 日報編集ハンドラー
  const handleEditReport = (reportId: number) => {
    // ナビゲーション前に検索条件が確実に保存されているか確認
    if (isSearchActive && searchParams) {
      // console.log('ナビゲーション前に検索条件を保存:', searchParams);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searchParams));
      setLastSavedParams(searchParams);
    }
    // 日報修正画面に遷移
    navigate(`/daily-report/${reportId}/edit`);
  };

  // 請求書作成依頼ボタンのクリックハンドラ
  const handleInvoiceRequestClick = (constructionId: number) => {
    setSelectedConstructionId(constructionId);
    setInvoiceRequestDialogOpen(true);
  };

  // 依頼成功時の処理
  const handleInvoiceRequestSuccess = () => {
    // 必要に応じて工事一覧を更新
    if (isSearchActive && searchParams) {
      searchConstructions(searchParams);
    } else {
      loadConstructions();
    }
  };

  return (
    <Container>
      <Box sx={{
        my: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Typography variant="h4" component="h1">
          工事一覧
        </Typography>

        <Box sx={{
          display: 'flex',
          gap: 2,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => setSearchModalOpen(true)}
            fullWidth
          >
            検索
          </Button>

          {isSearchActive && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FilterAltOffIcon />}
              onClick={handleResetSearch}
              fullWidth
            >
              条件クリア
            </Button>
          )}
        </Box>
      </Box>

      {/* 検索結果表示 */}
      {isSearchActive && (
        <Typography
          variant="subtitle1"
          sx={{
            mb: 2,
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <SearchIcon fontSize="small" />
          検索結果: {constructions.length}件
        </Typography>
      )}

      <ConstructionList
        constructions={constructions}
        loading={loading}
        error={error}
        page={page}
        rowsPerPage={rowsPerPage}
        expandedRows={expandedRows}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onToggleRowExpansion={toggleRowExpansion}
        onNewClick={() => navigate('/construction/new')}
        onDailyReportCreate={handleDailyReportCreate}
        onCalculationSheet={handleCalculationSheet}
        onEditReport={handleEditReport}
        onInvoiceRequestClick={handleInvoiceRequestClick}
      />

      {/* 検索モーダル */}
      <SearchModal
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        onSearch={handleSearch}
        members={members}
        customers={customers}
      // initialValuesはコンポーネントに応じて追加
      />

      {/* 請求書作成依頼ダイアログ */}
      {selectedConstructionId !== null && (
        <InvoiceRequestDialog
          open={invoiceRequestDialogOpen}
          onClose={() => setInvoiceRequestDialogOpen(false)}
          constructionId={selectedConstructionId}
          onSuccess={handleInvoiceRequestSuccess}
        />
      )}

      {/* スケジュール選択ダイアログ（新規追加） */}
      <ScheduleSelectionDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        constructionId={selectedConstructionId || 0}
        constructionName={selectedConstructionName}
        onScheduleSelect={handleScheduleSelect}
        initialSchedules={unreportedSchedules} // 取得済みのスケジュールを渡す
      />

      {/* 職長選択ダイアログ（新規追加） */}
      <SupervisorSelectionDialog
        open={supervisorDialogOpen}
        onClose={() => setSupervisorDialogOpen(false)}
        members={membersData}
        onSupervisorSelect={handleSupervisorSelect}
      />
    </Container>
  );
};

export default ConstructionListPage;